import {
  Form,
  Col,
  Button,
  Space,
  Input,
  Select,
  InputNumber,
  Radio,
  DatePicker,
} from 'antd';

import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';

import { WrapperSelect, BrickCodeInput } from 'common/components';

import { useGetProductEnums } from 'hooks';

import { NUMERIC_TYPE } from 'static/Constants';

import { findProductPropertyEnums } from 'pages/product-full-view/components/product-detail-view/utils';
import sortByKey from 'utils/sortByKey';

import { useRequiredActiveProductContext } from './hooks';

export const RequiredPropertyInput = ({ property, status }) => {
  const { productEnums } = useGetProductEnums();

  const { requiredForm } = useRequiredActiveProductContext();

  const { dataType, listName, isMultipleValue, fieldFullPath } = property;

  const lowercaseDataType = dataType.toLowerCase();
  const lowercaseNumberType = NUMERIC_TYPE.map((type) => type.toLowerCase());

  const isBrickCode = fieldFullPath === 'brickCode';
  const isBrickCodeName = fieldFullPath === 'brickCodeName';

  if (isBrickCode) {
    return (
      <RequiredBrickCode
        fieldFullPath={fieldFullPath}
        requiredForm={requiredForm}
      />
    );
  }

  if (isBrickCodeName) {
    return (
      <RequiredLayoutInput>
        <RequiredText fieldFullPath={fieldFullPath} status={status} disabled />
      </RequiredLayoutInput>
    );
  }

  if (listName) {
    const propertyEnums = findProductPropertyEnums(listName, productEnums);

    return (
      <RequiredLayoutInput>
        <RequiredSelect
          isMultipleValue={isMultipleValue}
          propertyEnums={propertyEnums}
          fieldFullPath={fieldFullPath}
          status={status}
        />
      </RequiredLayoutInput>
    );
  }

  if (isMultipleValue) {
    return (
      <RequiredLayoutInput>
        <RequiredList dataType={dataType} fieldFullPath={fieldFullPath} />
      </RequiredLayoutInput>
    );
  }

  if (lowercaseDataType === 'string') {
    return (
      <RequiredLayoutInput>
        <RequiredText fieldFullPath={fieldFullPath} status={status} />
      </RequiredLayoutInput>
    );
  }

  if (lowercaseNumberType.includes(lowercaseDataType)) {
    return (
      <RequiredLayoutInput>
        <RequiredNumber fieldFullPath={fieldFullPath} status={status} />
      </RequiredLayoutInput>
    );
  }

  if (lowercaseDataType === 'boolean') {
    return (
      <RequiredLayoutInput>
        <RequiredBoolean fieldFullPath={fieldFullPath} />
      </RequiredLayoutInput>
    );
  }

  if (lowercaseDataType === 'datetime') {
    return (
      <RequiredLayoutInput>
        <RequiredDatetime fieldFullPath={fieldFullPath} />
      </RequiredLayoutInput>
    );
  }

  return <RequiredLayoutInput>Under constructor</RequiredLayoutInput>;
};

const RequiredBrickCode = ({ fieldFullPath, requiredForm }) => {
  return (
    <>
      <RequiredFormItem fieldFullPath={fieldFullPath} hidden>
        render hidden input to collect brick code
      </RequiredFormItem>
      <BrickCodeInput
        updateBrickCodeData={(brickCodeValues) => {
          const currentFormValues = requiredForm.getFieldsValue();

          requiredForm.setFieldsValue({
            ...currentFormValues,
            ...brickCodeValues,
          });
        }}
      />
    </>
  );
};

const RequiredText = ({ fieldFullPath, disabled, status, ...otherProps }) => {
  return (
    <RequiredFormItem fieldFullPath={fieldFullPath} {...otherProps}>
      <Input status={status} disabled={disabled} />
    </RequiredFormItem>
  );
};

const RequiredNumber = ({ fieldFullPath, status, ...otherProps }) => {
  return (
    <RequiredFormItem fieldFullPath={fieldFullPath} {...otherProps}>
      <InputNumber status={status} style={{ width: '100%' }} />
    </RequiredFormItem>
  );
};

const RequiredBoolean = ({ fieldFullPath, ...otherProps }) => {
  return (
    <RequiredFormItem fieldFullPath={fieldFullPath} {...otherProps}>
      <Radio.Group>
        <Radio value={true}>True</Radio>
        <Radio value={false}>False</Radio>
      </Radio.Group>
    </RequiredFormItem>
  );
};

const RequiredDatetime = ({ fieldFullPath, ...otherProps }) => {
  return (
    <RequiredFormItem fieldFullPath={fieldFullPath} {...otherProps}>
      <DatePicker />
    </RequiredFormItem>
  );
};

const RequiredSelect = (props) => {
  const {
    propertyEnums,
    fieldFullPath,
    isMultipleValue,
    status,
    ...otherProps
  } = props;

  return (
    <RequiredFormItem fieldFullPath={fieldFullPath} {...otherProps}>
      <WrapperSelect
        showSearch
        mode={isMultipleValue ? 'multiple' : ''}
        filterOption={(input, option) =>
          option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
        }
        style={{ width: '100%' }}
        status={status}
      >
        {sortByKey(propertyEnums, 'enumDisplayName')?.map((property, index) => (
          <Select.Option
            value={property.enumDisplayName}
            key={property.enumDisplayName + index}
          >
            {property.enumDisplayName
              ? property.enumDisplayName
              : property.enumDescription}
          </Select.Option>
        ))}
      </WrapperSelect>
    </RequiredFormItem>
  );
};

const RequiredLayoutInput = ({ children, ...otherProps }) => {
  return (
    <Col span={6} {...otherProps}>
      {children}
    </Col>
  );
};

const RequiredFormItem = ({ fieldFullPath, children, ...otherProps }) => {
  return (
    <Form.Item label={null} name={fieldFullPath} noStyle {...otherProps}>
      {children}
    </Form.Item>
  );
};

const RequiredList = ({ fieldFullPath, dataType }) => {
  const isDatetimeType = dataType === 'datetime';
  const isNumberType = NUMERIC_TYPE.includes(dataType);

  return (
    <Form.List name={fieldFullPath}>
      {(fields, { add, remove }) => {
        return (
          <>
            <div>
              {fields.map((field) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Space>
                    <Form.Item {...field} noStyle>
                      {isDatetimeType ? (
                        <DatePicker />
                      ) : isNumberType ? (
                        <InputNumber />
                      ) : (
                        <Input style={{ width: '100%' }} />
                      )}
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                  </Space>
                </div>
              ))}
            </div>
            <div>
              <Button
                type='dashed'
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                Add value
              </Button>
            </div>
          </>
        );
      }}
    </Form.List>
  );
};
